import { useQuery } from '@tanstack/react-query';

import { logisticsApi } from 'api/logistics';
import { useDebouncedValue } from 'shared/hooks/useDebouncedValue';

export const associablePurchaseOrdersSearchQueryKeys = {
    all: ['crossDockPurchaseOrders', 'associablePurchaseOrders'],
    search: (q, limit) => [...associablePurchaseOrdersSearchQueryKeys.all, q, limit],
};

/**
 * @param {string} searchTerm
 * @param {{ debounceMs?: number, enabled?: boolean, limit?: number }} [options]
 */
export function useAssociablePurchaseOrdersSearch(searchTerm, options = {}) {
    const debounceMs = options.debounceMs ?? 200;
    const limit = options.limit ?? 20;
    const enabledOption = options.enabled !== false;
    const trimmedSearch = String(searchTerm ?? '').trim();
    const debouncedSearch = useDebouncedValue(trimmedSearch, debounceMs);
    const isDebouncing = trimmedSearch.length >= 1 && trimmedSearch !== debouncedSearch;
    const enabled = enabledOption && debouncedSearch.length >= 1;

    const query = useQuery({
        queryKey: associablePurchaseOrdersSearchQueryKeys.search(debouncedSearch, limit),
        enabled,
        queryFn: async ({ signal }) => {
            const response = await logisticsApi.searchAssociablePurchaseOrders(
                { q: debouncedSearch, limit },
                { signal },
            );
            if (!response?.status) {
                throw new Error(response?.msg ?? 'Failed to search purchase orders');
            }
            return response.data?.rows ?? [];
        },
        staleTime: 30 * 1000,
        gcTime: 5 * 60 * 1000,
    });

    return {
        ...query,
        isDebouncing,
    };
}
